import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress,
  Container, 
  TextField, 
  Typography, 
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  InputAdornment,
  Badge,
  IconButton
} from '@mui/material';
import AppHeader from './AppHeader'; // Import the new AppHeader component
import PageLayout from './common/PageLayout';
import { 
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  ShoppingCart as ShoppingCartIcon,
  Backspace as BackspaceIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { customers as customerService } from '../services/kioskApi';
import { getUsersFromCache } from '../utils/userCache';
import { useKeyboard } from '../context/KeyboardContext';
const countries = [
  { code: '+1', name: 'United States (+1)' }
  /*{ code: '+91', name: 'India (+91)' },
  { code: '+44', name: 'United Kingdom (+44)' },*/
];

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const NewDevoteeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, resetTimer, updateCart, setUserSelected } = useSession();
  const { cart = [], total = 0 } = location.state || session || {};
  // Determine origin for navigation context
  const origin = (location?.state && location.state.from) || session?.lastOrigin || 'products';
  const originPath = origin === 'kotilinga' ? '/kotilinga' : '/shop';
  const kb = useKeyboard();
  
  // State for form and user matching
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneCountryCode: '+1',
    phoneNumber: ''
  });

  // Optional additional info meta fields
  const [additionalInfo, setAdditionalInfo] = useState({
    gothram: '',
    spouse: '',
    familyMembers: ''
  });
  
  // State for existing users and loading
  const [existingUsers, setExistingUsers] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [includeAddress, setIncludeAddress] = useState(false);
  const [error, setError] = useState('');
  
  // Address state
  const [addressData, setAddressData] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });

  // Derived validation
  const zipValid = !includeAddress || String(addressData.zipCode || '').length === 5;

  // Reset timer on component mount and user interaction
  useEffect(() => {
    resetTimer();
  }, [resetTimer]);

  // Redirect if no cart data
  useEffect(() => {
    if (!cart || cart.length === 0) {
      navigate(originPath);
    }
  }, [cart, navigate, originPath]);

  // Handle email validation and check using API
  const handleEmailCheck = async () => {
    const email = formData.email.trim();
    
    // Basic validation
    if (!email) {
      setError('Email is required');
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setError('');
    setIsChecking(true);
    setExistingUsers([]);
    setHasSearched(false);
    
    try {
      // Call the API to search for users with this email
      const response = await customerService.searchUsers(email);
      
      if (response && Array.isArray(response)) {
        // Filter for exact email match (case-insensitive)
        const emailLower = email.toLowerCase();
        const matchingUsers = response.filter(user => {
          const userEmail = (user.email || '').toLowerCase();
          const billingEmail = (user.billing?.email || '').toLowerCase();
          return userEmail === emailLower || billingEmail === emailLower;
        });
        
        console.log(`Found ${matchingUsers.length} users with email ${email} via API`);
        
        if (matchingUsers.length > 0) {
          setExistingUsers(matchingUsers);
          setError('This email is already registered. Please select the account below or use a different email.');
        } else {
          setExistingUsers([]);
          setError('');
        }
      } else {
        console.error('Invalid response from API:', response);
        setError('Error validating email. Please try again.');
      }
    } catch (err) {
      console.error('Error checking email via API:', err);
      setError('Error checking email. Please try again.');
    } finally {
      setHasSearched(true);
      setIsChecking(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    // Enforce US phone number to be max 10 digits (numbers only)
    if (name === 'phoneNumber') {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, phoneNumber: digits }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle address input changes
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    // Sanitize postal/zip to 5 digits only
    if (name === 'zipCode') {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 5);
      setAddressData(prev => ({ ...prev, zipCode: digits }));
      return;
    }
    setAddressData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // On-screen keyboard open handlers
  const openEmailKeyboard = useCallback(() => {
    if (!kb) return;
    // If virtual keyboard is disabled, allow physical keyboard input to work normally
    if (kb.keyboardDisabled) {
      console.log('[NewDevoteeForm] Virtual keyboard disabled, physical keyboard will work normally');
      return;
    }
    kb.open('email', { position: 'below', widthPct: 0.85, heightVh: 35, showEnter: true, autoHeight: true, anchorOffsetPx: 100, hCenter: true }, {
      onInput: (ch) => setFormData(prev => ({ ...prev, email: (prev.email || '') + ch })),
      onBackspace: () => setFormData(prev => ({ ...prev, email: (prev.email || '').slice(0, -1) })),
      onEnter: () => {
        try { kb.close(); kb.suppress && kb.suppress(500); } catch {}
        // Click the same Continue button used in the UI to ensure identical behavior
        setTimeout(() => {
          const btn = document.querySelector('[data-email-continue="true"]');
          if (btn) btn.click(); else handleEmailCheck();
        }, 0);
      },
      onClear: () => setFormData(prev => ({ ...prev, email: '' }))
    });
  }, [kb, handleEmailCheck]);

  const openFirstNameKeyboard = useCallback(() => {
    if (!kb) return;
    // If virtual keyboard is disabled, allow physical keyboard input to work normally
    if (kb.keyboardDisabled) {
      console.log('[NewDevoteeForm] Virtual keyboard disabled, physical keyboard will work normally');
      return;
    }
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setFormData(prev => ({ ...prev, firstName: (prev.firstName || '') + ch })),
      onBackspace: () => setFormData(prev => ({ ...prev, firstName: (prev.firstName || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setFormData(prev => ({ ...prev, firstName: '' }))
    });
  }, [kb]);

  // Additional Info keyboards (optional fields)
  const openGothramKeyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setAdditionalInfo(prev => ({ ...prev, gothram: (prev.gothram || '') + ch })),
      onBackspace: () => setAdditionalInfo(prev => ({ ...prev, gothram: (prev.gothram || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setAdditionalInfo(prev => ({ ...prev, gothram: '' }))
    });
  }, [kb]);

  const openSpouseKeyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setAdditionalInfo(prev => ({ ...prev, spouse: (prev.spouse || '') + ch })),
      onBackspace: () => setAdditionalInfo(prev => ({ ...prev, spouse: (prev.spouse || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setAdditionalInfo(prev => ({ ...prev, spouse: '' }))
    });
  }, [kb]);

  const openFamilyMembersKeyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true, includeComma: true }, {
      onInput: (ch) => setAdditionalInfo(prev => ({ ...prev, familyMembers: (prev.familyMembers || '') + ch })),
      onBackspace: () => setAdditionalInfo(prev => ({ ...prev, familyMembers: (prev.familyMembers || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setAdditionalInfo(prev => ({ ...prev, familyMembers: '' }))
    });
  }, [kb]);

  // Address keyboards
  const openAddress1Keyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setAddressData(prev => ({ ...prev, address1: (prev.address1 || '') + ch })),
      onBackspace: () => setAddressData(prev => ({ ...prev, address1: (prev.address1 || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setAddressData(prev => ({ ...prev, address1: '' }))
    });
  }, [kb]);

  const openAddress2Keyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setAddressData(prev => ({ ...prev, address2: (prev.address2 || '') + ch })),
      onBackspace: () => setAddressData(prev => ({ ...prev, address2: (prev.address2 || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setAddressData(prev => ({ ...prev, address2: '' }))
    });
  }, [kb]);

  const openCityKeyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setAddressData(prev => ({ ...prev, city: (prev.city || '') + ch })),
      onBackspace: () => setAddressData(prev => ({ ...prev, city: (prev.city || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setAddressData(prev => ({ ...prev, city: '' }))
    });
  }, [kb]);

  const openZipKeyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('numeric', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: false, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setAddressData(prev => {
        const curr = String(prev.zipCode || '');
        if (!/^\d$/.test(ch) || curr.length >= 5) return prev; // digits only, max 5
        return { ...prev, zipCode: curr + ch };
      }),
      onBackspace: () => setAddressData(prev => ({ ...prev, zipCode: (prev.zipCode || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setAddressData(prev => ({ ...prev, zipCode: '' }))
    });
  }, [kb]);

  const openLastNameKeyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    kb.open('alpha', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: true, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setFormData(prev => ({ ...prev, lastName: (prev.lastName || '') + ch })),
      onBackspace: () => setFormData(prev => ({ ...prev, lastName: (prev.lastName || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setFormData(prev => ({ ...prev, lastName: '' }))
    });
  }, [kb]);

  const openPhoneKeyboard = useCallback(() => {
    if (!kb) return;
    if (kb.keyboardDisabled) return;
    // Show keypad directly below the registration box
    kb.open('numeric', { position: 'below', anchorSelector: '[data-kb-anchor="new-devotee-form"]', anchorOffsetPx: 64, widthPct: 0.9, heightVh: 35, showEnter: false, autoHeight: true, hCenter: true }, {
      onInput: (ch) => setFormData(prev => {
        const curr = String(prev.phoneNumber || '');
        // Allow only digits and cap at 10
        if (!/^\d$/.test(ch) || curr.length >= 10) return prev;
        return { ...prev, phoneNumber: curr + ch };
      }),
      onBackspace: () => setFormData(prev => ({ ...prev, phoneNumber: (prev.phoneNumber || '').slice(0, -1) })),
      onEnter: () => {},
      onClear: () => setFormData(prev => ({ ...prev, phoneNumber: '' }))
    });
  }, [kb]);

  // Handle user selection
  const handleSelectUser = (user) => {
    setSelectedUser(user);
  };

  // Handle continue with selected user
  const handleContinueWithUser = async (e, user) => {
    e.preventDefault();
    if (!user) return;
    
    console.log('Continuing with user:', user);
    resetTimer();
    
    try {
      // Set the user in the session context
      await setUserSelected(true, user);
      
      // Navigate to the payment page with the selected user and cart data
      navigate('/payment', { 
        state: { 
          cart,
          devotee: user,
          total,
          fromCart: true,
          from: origin 
        },
        replace: true
      });
    } catch (err) {
      console.error('Error setting user in session:', err);
      setError('Failed to set user session. Please try again.');
    }
  };

  // Handle form submission for new user
  const handleSubmit = async (e) => {
    e.preventDefault();
    resetTimer();
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    setIsCreating(true);
    setError('');

    const phoneCombined = formData.phoneNumber ? (formData.phoneCountryCode + formData.phoneNumber) : '';

    const meta = {
      billing_gothram: (additionalInfo.gothram || '').trim(),
      billing_spouse: (additionalInfo.spouse || '').trim(),
      // Send family members as raw trimmed string (no parsing)
      billing_family_members: (additionalInfo.familyMembers || '').trim()
    };
    const hasMeta = Object.values(meta).some(v => !!v);

    const customerData = {
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      ...(formData.phoneNumber ? { phone: phoneCombined } : {}),
      ...(includeAddress && {
        address1: addressData.address1,
        address2: addressData.address2,
        city: addressData.city,
        state: addressData.state,
        zipCode: addressData.zipCode,
        country: addressData.country
      }),
      ...(hasMeta ? { meta } : {})
    };

    console.log('Creating new devotee with data:', customerData);
    
    try {
      // Use the kioskApi service to create the customer via our backend
      const result = await customerService.create(customerData);
      console.log('Customer created successfully:', result);

      // Prepare the devotee data with all necessary fields
      const devoteeData = {
        id: result.id || Date.now(),
        customer_id: result.id || result.customer_id, // Ensure customer_id is set from the API response
        email: result.email || formData.email,
        first_name: result.first_name || formData.firstName,
        last_name: result.last_name || formData.lastName,
        phone: formData.phoneCountryCode + formData.phoneNumber,
        billing: {
          first_name: result.first_name || formData.firstName,
          last_name: result.last_name || formData.lastName,
          email: result.email || formData.email,
          phone: formData.phoneCountryCode + formData.phoneNumber,
          ...(includeAddress && {
            address_1: addressData.address1,
            address_2: addressData.address2,
            city: addressData.city,
            state: addressData.state,
            postcode: addressData.zipCode,
            country: addressData.country || 'US'
          })
        },
        ...(includeAddress && { address: addressData })
      };
      
      console.log('Created devotee data with customer_id:', devoteeData.customer_id, 'from result:', { 
        resultId: result.id, 
        resultCustomerId: result.customer_id 
      });

      // Calculate the total from the cart items
      const calculatedTotal = cart.reduce((sum, item) => {
        return sum + (parseFloat(item.price) * (item.quantity || 1));
      }, 0);
      
      console.log('Updating session with new devotee data...');
      
      // First, update the session with the new devotee data and add to users list
      await updateCart(cart, devoteeData);
      
      // Then set user as selected with the devotee data
      setUserSelected(true, devoteeData);
      
      // Add a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('Navigating to payment with data:', { 
        cart, 
        devotee: devoteeData, 
        total: calculatedTotal 
      });
      
      // Navigate to payment with the customer data
      navigate('/payment', {
        state: {
          cart: [...cart],
          devotee: { ...devoteeData },
          total: calculatedTotal,
          fromCart: true,
          from: origin
        },
        replace: true
      });

    } catch (err) {
      console.error('Error creating/retrieving customer:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsCreating(false);
      setIsChecking(false);
    }
  };

  // Loading overlay component
  const LoadingOverlay = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      }}
    >
      <CircularProgress size={60} thickness={4} sx={{ mb: 2, color: 'primary.main' }} />
      <Typography variant="h6" color="text.primary" sx={{ mt: 2, fontWeight: 'medium' }}>
        Creating your account...
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Please wait while we set up your account
      </Typography>
    </Box>
  );

  return (
    <PageLayout contentMaxWidth="xl">
      <Box sx={{ width: '100%', position: 'relative', px: 0, pb: (kb?.visible && kb?.options?.position === 'bottom') ? '38vh' : 0, transition: 'padding-bottom 200ms ease' }}>
      {isCreating && <LoadingOverlay />}
      <Box sx={{ width: '100%', margin: '0 auto' }}>
        {/* Top action buttons: Back (left), Home (center), Cart (right) */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/checkout', { state: { cart, fromNewDevotee: true, from: origin } })} 
              size="large" 
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#e1bee7',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              startIcon={<HomeIcon />} 
              onClick={() => navigate('/home')} 
              size="large" 
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#e1bee7',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Home
            </Button>
          </Box>
          <Box sx={{ flex: '0 0 auto' }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/cart', { state: { from: origin } })} 
              startIcon={
                <Badge badgeContent={cart?.length || 0} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              } 
              size="large" 
              sx={{
                minWidth: 200,
                px: 3,
                py: 1.2,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#f3e5f5',
                borderColor: '#d1c4e9',
                color: '#4a148c',
                borderWidth: 1.5,
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: '#e1bee7',
                  borderColor: '#b39ddb'
                }
              }}
            >
              Cart
            </Button>
          </Box>
        </Box>

        <Paper elevation={0} sx={{ p: 3, mb: kb?.visible ? '34vh' : 2, bgcolor: '#f3e5f5', border: '1px solid #e1bee7', borderRadius: 2, '& .MuiOutlinedInput-root': { bgcolor: '#ffffff' } }} data-kb-anchor="new-devotee-form">
          <Typography variant="h6" gutterBottom>New Devotee Registration</Typography>
          <form onSubmit={handleSubmit}>
            {/* Email Section - Always Visible */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Please enter below information.
              </Typography>
              {/* Put the label above the row so TextField and Button stay on the same line */}
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Email Address</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 2, alignItems: 'start' }}>
                <Box>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      const { value } = e.target;
                      setFormData(prev => ({ ...prev, email: value }));
                      // Reset state to allow entering a different email
                      setError('');
                      setExistingUsers([]);
                      setHasSearched(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEmailCheck();
                      }
                    }}
                    required
                    disabled={isChecking}
                    error={!!error}
                    helperText={error || ' '}
                    size="small"
                    onFocus={openEmailKeyboard}
                    InputProps={{ 
                      readOnly: !kb?.keyboardDisabled, // Only readonly when virtual keyboard is enabled
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="backspace"
                            edge="end"
                            size="small"
                            onClick={() => setFormData(prev => ({ ...prev, email: (prev.email || '').slice(0, -1) }))}
                            disabled={!formData.email}
                            sx={{ mr: 0.5 }}
                          >
                            <BackspaceIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="clear email"
                            edge="end"
                            size="small"
                            onClick={() => setFormData(prev => ({ ...prev, email: '' }))}
                            disabled={!formData.email}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) 
                    }}
                  />
                </Box>

                <Button 
                  variant="contained" 
                  onMouseDown={() => { try { kb?.close(); } catch {} }}
                  onClick={(e) => { try { kb?.suppress && kb.suppress(500); } catch {} handleEmailCheck(); }}
                  disabled={isChecking || !formData.email}
                  data-email-continue="true"
                  sx={{ height: 40, minWidth: 120, whiteSpace: 'nowrap', alignSelf: 'start' }}
                >
                  {isChecking ? 'Checking...' : 'Continue'}
                </Button>
              </Box>
              
              {/* Consolidated message is shown via the email field helper/error text above */}
            </Box>

            {/* Show user selection if we have matches */}
              {hasSearched && existingUsers.length > 0 && (
              <Box mb={3}>
                <List sx={{ 
                  mb: 2, 
                  maxHeight: 200, 
                  overflowY: 'scroll', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1 
                }}>
                  {existingUsers.map((user) => (
                    <ListItem 
                      key={user.id}
                      sx={{
                        borderBottom: '1px solid #f5f5f5',
                        '&:last-child': { borderBottom: 'none' },
                        bgcolor: selectedUser?.id === user.id ? 'action.selected' : 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: 'pointer',
                        borderRadius: 1,
                        pr: 14, // Make room for the button
                        position: 'relative',
                        alignItems: 'center',
                        minHeight: 64
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: selectedUser?.id === user.id ? 'primary.main' : 'grey.400' }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={`${user.first_name} ${user.last_name}`}
                        secondary={user.email}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectUser(user);
                          handleContinueWithUser(e, user);
                        }}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          minWidth: '160px',
                          whiteSpace: 'nowrap',
                          height: 36,
                          lineHeight: 1
                        }}
                        disableRipple
                        disableElevation
                      >
                        Select & Continue
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Show personal information section if email is validated and no existing users */}
            {hasSearched && existingUsers.length === 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
                  Your Information
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      First Name <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      disabled={isChecking}
                      size="small"
                      onFocus={openFirstNameKeyboard}
                      InputProps={{ 
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="backspace"
                              edge="end"
                              size="small"
                              onClick={() => setFormData(prev => ({ ...prev, firstName: (prev.firstName || '').slice(0, -1) }))}
                              disabled={!formData.firstName}
                              sx={{ mr: 0.5 }}
                            >
                              <BackspaceIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              aria-label="clear first name"
                              edge="end"
                              size="small"
                              onClick={() => setFormData(prev => ({ ...prev, firstName: '' }))}
                              disabled={!formData.firstName}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) 
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Last Name <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      disabled={isChecking}
                      size="small"
                      onFocus={openLastNameKeyboard}
                      InputProps={{ 
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="backspace"
                              edge="end"
                              size="small"
                              onClick={() => setFormData(prev => ({ ...prev, lastName: (prev.lastName || '').slice(0, -1) }))}
                              disabled={!formData.lastName}
                              sx={{ mr: 0.5 }}
                            >
                              <BackspaceIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              aria-label="clear last name"
                              edge="end"
                              size="small"
                              onClick={() => setFormData(prev => ({ ...prev, lastName: '' }))}
                              disabled={!formData.lastName}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) 
                      }}
                    />
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Phone Number (Optional)</Typography>
                    <TextField
                      fullWidth
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      // Optional phone number
                      size="small"
                      onFocus={openPhoneKeyboard}
                      InputProps={{ 
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="backspace"
                              edge="end"
                              size="small"
                              onClick={() => setFormData(prev => ({ ...prev, phoneNumber: (prev.phoneNumber || '').slice(0, -1) }))}
                              disabled={!formData.phoneNumber}
                              sx={{ mr: 0.5 }}
                            >
                              <BackspaceIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              aria-label="clear phone"
                              edge="end"
                              size="small"
                              onClick={() => setFormData(prev => ({ ...prev, phoneNumber: '' }))}
                              disabled={!formData.phoneNumber}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) 
                      }}
                    />
                  </Box>
                </Box>
                
                {/* Address toggle removed as requested */}

                {/* Additional Info (Optional) */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Additional Info (Optional)
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Gothram</Typography>
                      <TextField
                        fullWidth
                        name="gothram"
                        value={additionalInfo.gothram}
                        onChange={(e) => setAdditionalInfo(prev => ({ ...prev, gothram: e.target.value }))}
                        size="small"
                        onFocus={openGothramKeyboard}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="backspace"
                                edge="end"
                                size="small"
                                onClick={() => setAdditionalInfo(prev => ({ ...prev, gothram: (prev.gothram || '').slice(0, -1) }))}
                                disabled={!additionalInfo.gothram}
                                sx={{ mr: 0.5 }}
                              >
                                <BackspaceIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label="clear gothram"
                                edge="end"
                                size="small"
                                onClick={() => setAdditionalInfo(prev => ({ ...prev, gothram: '' }))}
                                disabled={!additionalInfo.gothram}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Spouse Name</Typography>
                      <TextField
                        fullWidth
                        name="spouse"
                        value={additionalInfo.spouse}
                        onChange={(e) => setAdditionalInfo(prev => ({ ...prev, spouse: e.target.value }))}
                        size="small"
                        onFocus={openSpouseKeyboard}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="backspace"
                                edge="end"
                                size="small"
                                onClick={() => setAdditionalInfo(prev => ({ ...prev, spouse: (prev.spouse || '').slice(0, -1) }))}
                                disabled={!additionalInfo.spouse}
                                sx={{ mr: 0.5 }}
                              >
                                <BackspaceIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label="clear spouse"
                                edge="end"
                                size="small"
                                onClick={() => setAdditionalInfo(prev => ({ ...prev, spouse: '' }))}
                                disabled={!additionalInfo.spouse}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Family Members (comma-separated)</Typography>
                  <TextField
                    fullWidth
                    name="familyMembers"
                    value={additionalInfo.familyMembers}
                    onChange={(e) => setAdditionalInfo(prev => ({ ...prev, familyMembers: e.target.value }))}
                    sx={{ mb: 2 }}
                    size="small"
                    onFocus={openFamilyMembersKeyboard}
                    InputProps={{
                      readOnly: !kb?.keyboardDisabled, // Only readonly when virtual keyboard is enabled
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="backspace"
                            edge="end"
                            size="small"
                            onClick={() => setAdditionalInfo(prev => ({ ...prev, familyMembers: (prev.familyMembers || '').slice(0, -1) }))}
                            disabled={!additionalInfo.familyMembers}
                            sx={{ mr: 0.5 }}
                          >
                            <BackspaceIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="clear family members"
                            edge="end"
                            size="small"
                            onClick={() => setAdditionalInfo(prev => ({ ...prev, familyMembers: '' }))}
                            disabled={!additionalInfo.familyMembers}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>

                {includeAddress && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Address Information (Optional)
                    </Typography>

                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Address Line 1</Typography>
                      <TextField
                        fullWidth
                        name="address1"
                        value={addressData.address1}
                        onChange={handleAddressChange}
                        sx={{ mb: 2 }}
                        size="small"
                        onFocus={openAddress1Keyboard}
                        InputProps={{ 
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="backspace"
                                edge="end"
                                size="small"
                                onClick={() => setAddressData(prev => ({ ...prev, address1: (prev.address1 || '').slice(0, -1) }))}
                                disabled={!addressData.address1}
                                sx={{ mr: 0.5 }}
                              >
                                <BackspaceIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label="clear address1"
                                edge="end"
                                size="small"
                                onClick={() => setAddressData(prev => ({ ...prev, address1: '' }))}
                                disabled={!addressData.address1}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) 
                        }}
                      />

                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Address Line 2</Typography>
                      <TextField
                        fullWidth
                        name="address2"
                        value={addressData.address2}
                        onChange={handleAddressChange}
                        sx={{ mb: 2 }}
                        size="small"
                        onFocus={openAddress2Keyboard}
                        InputProps={{ 
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="backspace"
                                edge="end"
                                size="small"
                                onClick={() => setAddressData(prev => ({ ...prev, address2: (prev.address2 || '').slice(0, -1) }))}
                                disabled={!addressData.address2}
                                sx={{ mr: 0.5 }}
                              >
                                <BackspaceIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                aria-label="clear address2"
                                edge="end"
                                size="small"
                                onClick={() => setAddressData(prev => ({ ...prev, address2: '' }))}
                                disabled={!addressData.address2}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) 
                        }}
                      />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>City</Typography>
                        <TextField
                          fullWidth
                          name="city"
                          value={addressData.city}
                          onChange={handleAddressChange}
                          size="small"
                          onFocus={openCityKeyboard}
                          InputProps={{ 
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="backspace"
                                  edge="end"
                                  size="small"
                                  onClick={() => setAddressData(prev => ({ ...prev, city: (prev.city || '').slice(0, -1) }))}
                                  disabled={!addressData.city}
                                  sx={{ mr: 0.5 }}
                                >
                                  <BackspaceIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  aria-label="clear city"
                                  edge="end"
                                  size="small"
                                  onClick={() => setAddressData(prev => ({ ...prev, city: '' }))}
                                  disabled={!addressData.city}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ) 
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>State</Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            name="state"
                            value={addressData.state}
                            onChange={handleAddressChange}
                          >
                            {states.map((state) => (
                              <MenuItem key={state} value={state}>
                                {state}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>ZIP/Postal Code</Typography>
                        <TextField
                          fullWidth
                          name="zipCode"
                          value={addressData.zipCode}
                          onChange={(e) => {
                            const zipCode = e.target.value;
                            if (zipCode.length <= 5 && /^\d*$/.test(zipCode)) {
                              handleAddressChange(e);
                            }
                          }}
                          error={false}
                          helperText={' '}
                          size="small"
                          onFocus={openZipKeyboard}
                          InputProps={{ 
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="backspace"
                                  edge="end"
                                  size="small"
                                  onClick={() => setAddressData(prev => ({ ...prev, zipCode: (prev.zipCode || '').slice(0, -1) }))}
                                  disabled={!addressData.zipCode}
                                  sx={{ mr: 0.5 }}
                                >
                                  <BackspaceIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  aria-label="clear zip"
                                  edge="end"
                                  size="small"
                                  onClick={() => setAddressData(prev => ({ ...prev, zipCode: '' }))}
                                  disabled={!addressData.zipCode}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ) 
                          }}
                        />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Country</Typography>
                        <TextField
                          fullWidth
                          name="country"
                          value={addressData.country}
                          onChange={handleAddressChange}
                          disabled
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
                
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isChecking || !formData.firstName || !formData.lastName || (includeAddress && !zipValid)}
                    sx={{ minWidth: 200 }}
                  >
                    {isChecking ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </Box>
              </>
            )}
          </form>
        </Paper>
      </Box>
      </Box>
    </PageLayout>
  );
};

export default NewDevoteeForm;



